import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routeSlugs, type Lang, getLocalizedPath } from '@/i18n/routes';
import { getSolutionsMenu } from '@/config/solutions-menu';
import { useEffect, useMemo } from 'react';

// Lazy imports for public pages
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const AboutPage = lazy(() => import('@/pages/public/AboutPage'));
const ContactPage = lazy(() => import('@/pages/public/ContactPage'));
const RepairStatusPage = lazy(() => import('@/pages/public/RepairStatusPage'));
const PublicInsuranceSignupPage = lazy(() => import('@/pages/public/PublicInsuranceSignupPage'));
const VehicleRegistrationPage = lazy(() => import('@/pages/public/VehicleRegistrationPage'));
const LocalDirectoryPage = lazy(() => import('@/pages/public/LocalDirectoryPage'));
const LocalLandingPage = lazy(() => import('@/pages/public/LocalLandingPage'));
const LegalPage = lazy(() => import('@/pages/LegalPage'));
const BlogPage = lazy(() => import('@/pages/public/BlogPage'));
const BlogPostPage = lazy(() => import('@/pages/public/BlogPostPage'));
const ShopPage = lazy(() => import('@/pages/public/ShopPage'));
const ShopCartPage = lazy(() => import('@/pages/public/ShopCartPage'));
const ShopCheckoutPage = lazy(() => import('@/pages/public/ShopCheckoutPage'));
const ShopWishlistPage = lazy(() => import('@/pages/public/ShopWishlistPage'));
const ShopComparePage = lazy(() => import('@/pages/public/ShopComparePage'));
const ShopLoginPage = lazy(() => import('@/pages/public/ShopLoginPage'));
const ShopCategoryPage = lazy(() => import('@/pages/public/ShopCategoryPage'));
const ProTipsPage = lazy(() => import('@/pages/public/ProTipsPage'));
const FAQPage = lazy(() => import('@/pages/public/FAQPage'));
const EmployeePortalPage = lazy(() => import('@/pages/public/EmployeePortalPage'));
const ERPMainLandingPage = lazy(() => import('@/pages/public/ERPMainLandingPage'));
const ModuleLandingPage = lazy(() => import('@/pages/public/ModuleLandingPage'));
const UseCaseLandingPage = lazy(() => import('@/pages/public/UseCaseLandingPage'));
const SitemapPage = lazy(() => import('@/pages/public/SitemapPage'));
const NotFound = lazy(() => import('@/pages/NotFound'));
function NavigateWithSlug({ to, replace }: { to: string; replace?: boolean }) {
  const { slug } = useParams();
  return <Navigate to={`${to}/${slug}`} replace={replace} />;
}

/**
 * Renders all public routes for a specific non-default language.
 * Each route uses the translated slug from routeSlugs.
 * The component also sets the i18n language on mount.
 */
export function I18nRoutes({ lang }: { lang: Lang }) {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const solutions = useMemo(() => getSolutionsMenu(lang), [lang]);

  return (
    <Routes>
      {/* Home */}
      <Route index element={<LandingPage />} />

      {/* About */}
      <Route path={routeSlugs.about[lang]} element={<AboutPage />} />

      {/* Contact */}
      <Route path={routeSlugs.contact[lang]} element={<ContactPage />} />

      {/* Insurance */}
      <Route path={routeSlugs.insurance[lang]} element={<PublicInsuranceSignupPage />} />

      {/* Repair Status */}
      <Route path={routeSlugs.repairStatus[lang]} element={<RepairStatusPage />} />

      {/* Registration */}
      <Route path={routeSlugs.registration[lang]} element={<VehicleRegistrationPage />} />

      {/* Directory */}
      <Route path="directory" element={<Navigate to={`/${lang}/${routeSlugs.directory[lang]}`} replace />} />
      <Route path="annuaire" element={<Navigate to={`/${lang}/${routeSlugs.directory[lang]}`} replace />} />
      <Route path="diretorio" element={<Navigate to={`/${lang}/${routeSlugs.directory[lang]}`} replace />} />
      <Route path="دليل" element={<Navigate to={`/${lang}/${routeSlugs.directory[lang]}`} replace />} />
      <Route path={routeSlugs.directory[lang]} element={<LocalDirectoryPage />} />

      {/* Local Shop */}
      <Route path={`${routeSlugs.localShop[lang]}/:slug`} element={<LocalLandingPage />} />

      {/* Blog */}
      <Route path={routeSlugs.blog[lang]} element={<BlogPage />} />
      <Route path={`${routeSlugs.blog[lang]}/:slug`} element={<BlogPostPage />} />

      {/* Legal */}
      <Route path={routeSlugs.legal[lang]} element={<LegalPage />} />

      {/* Pro Tips */}
      <Route path={routeSlugs.proTips[lang]} element={<ProTipsPage />} />

      {/* FAQ */}
      <Route path={routeSlugs.faq[lang]} element={<FAQPage />} />

      {/* Shop (ecommerce) */}
      <Route path={routeSlugs.shop[lang]} element={<ShopPage />} />
      <Route path={routeSlugs.shopCart[lang]} element={<ShopCartPage />} />
      <Route path={routeSlugs.shopCheckout[lang]} element={<ShopCheckoutPage />} />
      <Route path={routeSlugs.shopWishlist[lang]} element={<ShopWishlistPage />} />
      <Route path={routeSlugs.shopCompare[lang]} element={<ShopComparePage />} />
      <Route path={routeSlugs.shopLogin[lang]} element={<ShopLoginPage />} />
      <Route path={`${routeSlugs.shop[lang]}/*`} element={<ShopCategoryPage />} />

      {/* ERP Main Landing */}
      <Route path={routeSlugs.erpMain[lang]} element={<ERPMainLandingPage />} />

      {/* Dynamic Solutions/UseCases from Menu Config to ensure consistency */}
      {solutions.map((sol) => {
        let path = sol.to;
        // Remove lang prefix if present for the Route path
        if (path.startsWith(`/${lang}/`)) {
          path = path.replace(`/${lang}/`, '');
        } else if (path.startsWith(`/${lang}`)) {
          path = path.replace(`/${lang}`, '');
        } else if (path.startsWith('/')) {
          path = path.slice(1);
        }
        
        const Component = sol.slugBase === 'moduleLanding' ? ModuleLandingPage : UseCaseLandingPage;
        if (sol.slugBase === 'erpMain') return null; // Already handled above

        return (
          <Route key={sol.to} path={path} element={<Component />} />
        );
      })}

      {/* Legacy and fallback routes for solutions */}
      <Route path="modulo/:slug" element={<NavigateWithSlug to={`/${lang}/${routeSlugs.moduleLanding[lang]}`} replace />} />
      <Route path="solucion/:slug" element={<NavigateWithSlug to={`/${lang}/${routeSlugs.moduleLanding[lang]}`} replace />} />
      <Route path="solutions/:slug" element={<NavigateWithSlug to={`/${lang}/${routeSlugs.moduleLanding[lang]}`} replace />} />
      <Route path="modulo" element={<Navigate to={`/${lang}/${routeSlugs.moduleLanding[lang]}`} replace />} />
      <Route path="solucion" element={<Navigate to={`/${lang}/${routeSlugs.moduleLanding[lang]}`} replace />} />
      <Route path="solutions" element={<Navigate to={`/${lang}/${routeSlugs.moduleLanding[lang]}`} replace />} />
      
      <Route path={`${routeSlugs.moduleLanding[lang]}/erp-tienda-taller-patinetes`} element={<Navigate to={`/${lang}/${routeSlugs.erpMain[lang]}`} replace />} />
      <Route path={`${routeSlugs.moduleLanding[lang]}/erp-scooter-shop-workshop`} element={<Navigate to={`/${lang}/${routeSlugs.erpMain[lang]}`} replace />} />
      <Route path={`${routeSlugs.moduleLanding[lang]}/:slug`} element={<ModuleLandingPage />} />
      <Route path="gestion-taller-patinetes-electricos" element={<NavigateWithSlug to={`/${lang}/${routeSlugs.moduleLanding[lang]}`} replace />} />


      {/* Sitemap */}
      <Route path={routeSlugs.sitemap[lang]} element={<SitemapPage />} />
      <Route path="gestion-taller-patinetes-electricos" element={<NavigateWithSlug to={`/${lang}/${routeSlugs.moduleLanding[lang]}`} replace />} />

      {/* Employee Portal */}
      <Route path={routeSlugs.employeePortal[lang]} element={<EmployeePortalPage />} />

      {/* Catch-all for this language prefix */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
